import { PressableScale } from "pressto";
import { View, Text, Banner } from "@src/components";
import { Icon } from "@src/components/Icon";
import { useUnistyles } from "react-native-unistyles";
import { RoutinePreview } from "./RoutinePreview";
import { ExceptionsPreview } from "./ExceptionsPreview";
import { ResponsePreview } from "./ResponsePreview";
import { TimezonePreview } from "./TimezonePreview";
import type { AlarmRoutine, AlarmException, ArmedResponse } from "@src/services/api/types";

interface AlarmConfigSectionsProps {
  pristine: boolean;
  firstScheduleApplying: boolean;
  schedulesDiffer: boolean;
  showTimezoneMismatchBanner: boolean;
  timezone: string;
  cameraTimezone: string;
  userTimezone: string;
  routine: AlarmRoutine | null;
  exceptions: AlarmException[];
  response: ArmedResponse | null;
  onTimezonePress: () => void;
  onRoutinePress: () => void;
  onAddExceptionPress: () => void;
  onExceptionPress: (exceptionId: string) => void;
  onResponsePress: () => void;
  onAppliedSchedulePress?: () => void;
  variant: "mobile" | "web-sidepanel";
}

export function AlarmConfigSections({
  pristine,
  firstScheduleApplying,
  schedulesDiffer,
  showTimezoneMismatchBanner,
  timezone,
  cameraTimezone,
  userTimezone,
  routine,
  exceptions,
  response,
  onTimezonePress,
  onRoutinePress,
  onAddExceptionPress,
  onExceptionPress,
  onResponsePress,
  onAppliedSchedulePress,
  variant,
}: AlarmConfigSectionsProps) {
  const { theme } = useUnistyles();
  const isMobile = variant === "mobile";

  return (
    <>
      {pristine && (
        <View px={isMobile ? "5xl" : undefined} mb={isMobile ? "none" : "7xl"}>
          <Text textStyle="sm/medium" mb="3xl">
            Timezone
          </Text>
          <TimezonePreview timezone={null} onPress={onTimezonePress} accessoryIcon="chevronRight" />
        </View>
      )}

      {firstScheduleApplying && (
        <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : undefined} mb={isMobile ? "none" : "7xl"}>
          <View
            borderWidth={1}
            borderRadius={theme.spacing.lg}
            p="3xl"
            style={{ borderColor: theme.colors.warning[500], backgroundColor: theme.colors.warning[50] }}
          >
            <Text textStyle="sm/medium">Applying changes</Text>
            <Text textStyle="xs/regular" color="textMuted" mt="xs">
              We&apos;re applying your camera&apos;s first alarm configuration.
            </Text>
          </View>
        </View>
      )}

      {schedulesDiffer && (
        <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : undefined} mb={isMobile ? "none" : "7xl"}>
          <PressableScale onPress={onAppliedSchedulePress}>
            <View
              borderWidth={1}
              borderRadius={theme.spacing.lg}
              p="3xl"
              style={{ borderColor: theme.colors.blue[500], backgroundColor: theme.colors.blue[50] }}
            >
              <Text textStyle="sm/medium">Applying changes</Text>
              <Text textStyle="xs/regular" color="textMuted" mt="xs">
                We&apos;re applying your changes to the camera.
              </Text>
            </View>
          </PressableScale>
        </View>
      )}

      <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : undefined} mb={isMobile ? "none" : "7xl"}>
        <RoutinePreview routine={routine} onPress={onRoutinePress} accessoryIcon="chevronRight" disabled={pristine} />
      </View>

      {!pristine && (
        <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : undefined} mb={isMobile ? "none" : "7xl"}>
          <View flexDirection="row" justifyContent="space-between" alignItems="center" mb="xl">
            <Text textStyle="sm/medium">Exceptions</Text>
            <PressableScale onPress={onAddExceptionPress}>
              <View height={36} w={36} justifyContent="center" alignItems="center">
                <Icon name="plus" size={20} color={theme.colors.icon} />
              </View>
            </PressableScale>
          </View>
          <ExceptionsPreview
            exceptions={exceptions}
            timezone={timezone}
            onExceptionPress={onExceptionPress}
            accessoryIcon="chevronRight"
          />
        </View>
      )}

      <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : undefined}>
        <Text textStyle="sm/medium" mb="3xl">
          Alarm
        </Text>
        <ResponsePreview response={response} onPress={onResponsePress} accessoryIcon="chevronRight" />
      </View>

      <View px={isMobile ? "5xl" : undefined} mt={isMobile ? "4xl" : "7xl"}>
        <Text textStyle="sm/medium" mb="3xl">
          Timezone
        </Text>
        {showTimezoneMismatchBanner && (
          <View mb="3xl">
            <Banner
              variant="warning"
              title="Timezone difference"
              message="All dates and times in alarm settings are shown in the camera's local time."
            />
          </View>
        )}
        <Text color="textMuted" textStyle="sm/regular">
          Camera: {cameraTimezone}
        </Text>
        <Text color="textMuted" textStyle="sm/regular" mb={isMobile ? "4xl" : undefined}>
          You: {userTimezone}
        </Text>
      </View>
    </>
  );
}
